/**
 * Optimiciti API Specification
 * Welcome to the Beta release of the Optimiciti API. This API allows registered users to make direct calls to our optimization solvers via a web request.  Use of the API requires acceptance of our terms of service.  Some useful links: - [Optimiciti Homepage](https://optimiciti.com) - [Optimiciti API Access Plans](https://optimiciti.com/pricing)
 *
 * The version of the OpenAPI document: 0.0.2
 * Contact: api@optimiciti.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 *
 */

import ApiClient from '../ApiClient';

/**
 * The TravelCost model module.
 * @module model/TravelCost
 * @version 0.0.2
 */
class TravelCost {
    /**
     * Constructs a new <code>TravelCost</code>.
     * This schema defines a travel cost between two locations. The travel cost can be used to specify the duration, distance, and cost of travel between two locations. An entry is required in this table for each pair of locations in the locations table.
     * @alias module:model/TravelCost
     * @param fromLocationId {String} The locationId of the origin location for this travel cost. This locationId must exist in the Locations passed in ProblemData for this problem.
     * @param toLocationId {String} The locationId of the destination location for this travel cost. This locationId must exist in the Locations passed in ProblemData for this problem.
     */
    constructor(fromLocationId, toLocationId) { 
        
        TravelCost.initialize(this, fromLocationId, toLocationId);
    }

    /**
     * Initializes the fields of this object.
     * This method is used by the constructors of any subclasses, in order to implement multiple inheritance (mix-ins).
     * Only for internal use.
     */
    static initialize(obj, fromLocationId, toLocationId) { 
        obj['fromLocationId'] = fromLocationId;
        obj['toLocationId'] = toLocationId;
    }

    /**
     * Constructs a <code>TravelCost</code> from a plain JavaScript object, optionally creating a new instance.
     * Copies all relevant properties from <code>data</code> to <code>obj</code> if supplied or a new instance if not.
     * @param {Object} data The plain JavaScript object bearing properties of interest.
     * @param {module:model/TravelCost} obj Optional instance to populate.
     * @return {module:model/TravelCost} The populated <code>TravelCost</code> instance.
     */
    static constructFromObject(data, obj) {
        if (data) {
            obj = obj || new TravelCost();

            if (data.hasOwnProperty('fromLocationId')) {
                obj['fromLocationId'] = ApiClient.convertToType(data['fromLocationId'], 'String');
            }
            if (data.hasOwnProperty('toLocationId')) {
                obj['toLocationId'] = ApiClient.convertToType(data['toLocationId'], 'String');
            }
            if (data.hasOwnProperty('vehicleTypeId')) {
                obj['vehicleTypeId'] = ApiClient.convertToType(data['vehicleTypeId'], 'String');
            }
            if (data.hasOwnProperty('vehicleType')) {
                obj['vehicleType'] = ApiClient.convertToType(data['vehicleType'], 'String');
            }
            if (data.hasOwnProperty('duration')) {
                obj['duration'] = ApiClient.convertToType(data['duration'], 'Number');
            }
            if (data.hasOwnProperty('distance')) {
                obj['distance'] = ApiClient.convertToType(data['distance'], 'Number');
            }
            if (data.hasOwnProperty('cost')) {
                obj['cost'] = ApiClient.convertToType(data['cost'], 'Number');
            }
        }
        return obj;
    }

    /**
     * Validates the JSON data with respect to <code>TravelCost</code>.
     * @param {Object} data The plain JavaScript object bearing properties of interest.
     * @return {boolean} to indicate whether the JSON data is valid with respect to <code>TravelCost</code>.
     */
    static validateJSON(data) {
        // check to make sure all required properties are present in the JSON string
        for (const property of TravelCost.RequiredProperties) {
            if (!data.hasOwnProperty(property)) {
                throw new Error("The required field `" + property + "` is not found in the JSON data: " + JSON.stringify(data));
            }
        }
        // ensure the json data is a string
        if (data['fromLocationId'] && !(typeof data['fromLocationId'] === 'string' || data['fromLocationId'] instanceof String)) {
            throw new Error("Expected the field `fromLocationId` to be a primitive type in the JSON string but got " + data['fromLocationId']);
        }
        // ensure the json data is a string
        if (data['toLocationId'] && !(typeof data['toLocationId'] === 'string' || data['toLocationId'] instanceof String)) {
            throw new Error("Expected the field `toLocationId` to be a primitive type in the JSON string but got " + data['toLocationId']);
        }
        // ensure the json data is a string
        if (data['vehicleTypeId'] && !(typeof data['vehicleTypeId'] === 'string' || data['vehicleTypeId'] instanceof String)) {
            throw new Error("Expected the field `vehicleTypeId` to be a primitive type in the JSON string but got " + data['vehicleTypeId']);
        }
        // ensure the json data is a string
        if (data['vehicleType'] && !(typeof data['vehicleType'] === 'string' || data['vehicleType'] instanceof String)) {
            throw new Error("Expected the field `vehicleType` to be a primitive type in the JSON string but got " + data['vehicleType']);
        }

        return true;
    }


}

TravelCost.RequiredProperties = ["fromLocationId", "toLocationId"];

/**
 * The locationId of the origin location for this travel cost. This locationId must exist in the Locations passed in ProblemData for this problem.
 * @member {String} fromLocationId
 */
TravelCost.prototype['fromLocationId'] = undefined;

/**
 * The locationId of the destination location for this travel cost. This locationId must exist in the Locations passed in ProblemData for this problem.
 * @member {String} toLocationId
 */
TravelCost.prototype['toLocationId'] = undefined;

/**
 * The vehicleTypeId for this travel cost. This vehicleTypeId must exist in the VehicleTypes passed in ProblemData for this problem. If null, then the travel cost will apply to all vehicle types.
 * @member {String} vehicleTypeId
 */
TravelCost.prototype['vehicleTypeId'] = undefined;

/**
 * The name of the vehicle type for this travel cost. This field is optional and is provided for convenience only.
 * @member {String} vehicleType
 */
TravelCost.prototype['vehicleType'] = undefined;

/**
 * The travel duration between these two locations. If using integer time, then this should be in integer time units. If using clock time, then this should provided in seconds. If null, then the travel duration will be calculated automatically based on the latitude and longitude of the locations.
 * @member {Number} duration
 */
TravelCost.prototype['duration'] = undefined;

/**
 * The travel distance between these two locations. If null, then the travel distance will be calculated automatically based on the latitude and longitude of the locations. Any arbitrary unit can be used for distance, but the unit should be consistent across all entries.
 * @member {Number} distance
 */
TravelCost.prototype['distance'] = undefined;

/**
 * The cost of travel between these two locations.
 * @member {Number} cost
 */
TravelCost.prototype['cost'] = undefined;






export default TravelCost;

